import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import type { ResolvedStyle } from './OverrideResolver';
import { createGradientSky, createPanoramaSky, type GradientSky, type PanoramaSky } from './Sky';
import {
  applyEnvironmentStyle,
  applyLiveSkyStyle,
  applySkySceneStyle,
  applySunRigStyle,
  configureSunShadow,
} from './SceneLighting';
import type { StyleConfig } from './StyleConfig';
import { isWebGLRenderer, type AppRenderer, type WebGPUPMREMGeneratorCtor } from './Renderer';
import { installWebGPUAerialFogNode } from './AerialPerspective';

/**
 * Single source of truth for the sky + fog + sun-rig + environment boot.
 *
 * GAME (main.ts), MAP (TrackReview.ts) and RIG (RigReview.ts) all call this
 * with the same args. By construction the three modes cannot drift on
 * lighting / sky / fog / shadows. Anything that needs to vary per-mode lives
 * outside the rig (camera, racer, HUD, post-fx pipeline).
 *
 * The returned handles are wired into the scene; callers add them to render
 * loops and pass them through `applyResolvedSceneStyle` updates.
 */

export interface SceneStyleRigOptions {
  scene: THREE.Scene;
  renderer: AppRenderer;
  styleConfig: StyleConfig;
  resolvedStyle: ResolvedStyle;
  canyonV3: boolean;
  webgpuPMREMGeneratorCtor?: WebGPUPMREMGeneratorCtor | null;
}

export interface SceneShadowConfig {
  enabled: boolean;
  frustumSize: number;
  near: number;
  far: number;
  mapSize: number;
  softRadius: number;
  forwardBias: number;
}

export interface SceneStyleRig {
  sky: GradientSky | PanoramaSky;
  worldFog: THREE.FogExp2;
  sun: THREE.DirectionalLight;
  sunTarget: THREE.Object3D;
  fillSun: THREE.DirectionalLight;
  fillSunTarget: THREE.Object3D;
  hemisphere: THREE.HemisphereLight;
  bounceLight: THREE.DirectionalLight;
  bounceTarget: THREE.Object3D;
  rimLight: THREE.DirectionalLight;
  rimTarget: THREE.Object3D;
  shadowConfig: SceneShadowConfig;
  applyShadowConfig: () => void;
}

export function bootSceneStyleRig({
  scene,
  renderer,
  styleConfig,
  resolvedStyle,
  canyonV3,
  webgpuPMREMGeneratorCtor = null,
}: SceneStyleRigOptions): SceneStyleRig {
  const rendererMode = isWebGLRenderer(renderer) ? 'webgl2' : 'webgpu';
  const sky = styleConfig.sky.panoramaUrl
    ? createPanoramaSky(styleConfig.sky.panoramaUrl, { rendererMode })
    : createGradientSky(rendererMode);
  applyLiveSkyStyle(sky, resolvedStyle);
  scene.add(sky.mesh);

  // Fog comes from StyleConfig.fog. applySkySceneStyle handles colorHex /
  // density / followSky semantics so map view and game scene match exactly.
  const worldFog = new THREE.FogExp2(resolvedStyle.fog.colorHex, resolvedStyle.fog.density);
  scene.fog = worldFog;
  // WebGL injects sun-tinted aerial perspective per-material via
  // `installAerialPerspective` (onBeforeCompile). That path can't run on
  // WebGPU, so install a TSL `scene.fogNode` that mirrors the same shader.
  // Without this WebGPU mode falls back to flat `FogExp2` colour, which buries
  // the sky and looks like overcrowded brown haze.
  if (rendererMode === 'webgpu') installWebGPUAerialFogNode(scene);
  renderer.setClearColor(sky.horizonColor, 1);
  applySkySceneStyle(renderer, scene, worldFog, sky.horizonColor, resolvedStyle);

  const sun = new THREE.DirectionalLight(resolvedStyle.sunRig.key.colorHex, resolvedStyle.sunRig.key.intensity);
  // Live-tunable shadow config. Values mirror MAP (TrackReview.ts) so GAME
  // and MAP render the same contact-shadow / canyon-shadow integration.
  // WebGPU's shadow render path is materially faster than WebGL's, so we
  // can afford a 2× larger shadow map without spending the frame budget.
  // Texel density under the receiver is `2 × frustumSize / mapSize` metres
  // per shadow texel — bumping mapSize from 1024 → 2048 cuts the texel
  // size in half, killing the staircase aliasing on canyon ridge shadows.
  const shadowConfig: SceneShadowConfig = {
    enabled: true,
    frustumSize: canyonV3 ? 60 : 70,
    near: canyonV3 ? 12 : 20,
    far: canyonV3 ? 245 : 220,
    mapSize: rendererMode === 'webgpu' ? 2048 : 1024,
    softRadius: canyonV3 ? 1 : 2,
    forwardBias: 0,
  };
  const applyShadowConfig = (): void => {
    configureSunShadow(
      sun,
      canyonV3 ? -0.00002 : 0.00004,
      canyonV3 ? 0.026 : 0.018,
      {
        radius: shadowConfig.softRadius,
        size: shadowConfig.frustumSize,
        near: shadowConfig.near,
        far: shadowConfig.far,
        mapSize: shadowConfig.mapSize,
      },
    );
    sun.castShadow = shadowConfig.enabled;
    sun.shadow.map?.dispose();
    sun.shadow.map = null;
  };
  applyShadowConfig();
  scene.add(sun);
  const sunTarget = new THREE.Object3D();
  scene.add(sunTarget);
  sun.target = sunTarget;

  const fillSun = new THREE.DirectionalLight(resolvedStyle.sunRig.fill.colorHex, resolvedStyle.sunRig.fill.intensity);
  fillSun.castShadow = false;
  scene.add(fillSun);
  const fillSunTarget = new THREE.Object3D();
  scene.add(fillSunTarget);
  fillSun.target = fillSunTarget;

  const hemisphere = new THREE.HemisphereLight(
    resolvedStyle.sunRig.hemisphere.skyColorHex,
    resolvedStyle.sunRig.hemisphere.groundColorHex,
    resolvedStyle.sunRig.hemisphere.intensity,
  );
  scene.add(hemisphere);

  const bounceLight = new THREE.DirectionalLight(resolvedStyle.sunRig.bounce.colorHex, resolvedStyle.sunRig.bounce.intensity);
  bounceLight.castShadow = false;
  scene.add(bounceLight);
  const bounceTarget = new THREE.Object3D();
  scene.add(bounceTarget);
  bounceLight.target = bounceTarget;

  const rimLight = new THREE.DirectionalLight(resolvedStyle.sunRig.rim.colorHex, resolvedStyle.sunRig.rim.intensity);
  rimLight.castShadow = false;
  scene.add(rimLight);
  const rimTarget = new THREE.Object3D();
  scene.add(rimTarget);
  rimLight.target = rimTarget;

  applySunRigStyle(sun, fillSun, hemisphere, bounceLight, rimLight, resolvedStyle);

  if (isWebGLRenderer(renderer)) {
    const pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  } else if (webgpuPMREMGeneratorCtor) {
    const pmrem = new webgpuPMREMGeneratorCtor(renderer);
    scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  } else {
    console.warn('[webgpu] RoomEnvironment PMREM unavailable; GLTF IBL is disabled.');
    scene.environment = null;
  }
  applyEnvironmentStyle(scene, resolvedStyle);

  return {
    sky,
    worldFog,
    sun,
    sunTarget,
    fillSun,
    fillSunTarget,
    hemisphere,
    bounceLight,
    bounceTarget,
    rimLight,
    rimTarget,
    shadowConfig,
    applyShadowConfig,
  };
}
